import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge } from '@components';
import { PhoneNumber } from '../types';

interface PhoneNumbersTableProps {
  phoneNumbers: PhoneNumber[];
  onDelete: (id: string) => void;
  onBuyNumber: () => void;
  searchTerm: string;
  statusFilter: string;
}

export const PhoneNumbersTable: React.FC<PhoneNumbersTableProps> = ({
  phoneNumbers,
  onDelete,
  onBuyNumber,
  searchTerm,
  statusFilter,
}) => {
  const { t } = useTranslation();
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_use':
        return (
          <Badge variant="success" size="sm">
            {t('phoneNumbers.inUse')}
          </Badge>
        );
      case 'not_in_use':
        return (
          <Badge variant="warning" size="sm">
            {t('phoneNumbers.notInUse')}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" size="sm">
            {status}
          </Badge>
        );
    }
  };

  const filteredPhoneNumbers = phoneNumbers.filter(phone => {
    const matchesSearch = phone.number?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const matchesStatus = statusFilter === '' || phone.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Card>
      <CardContent>
        <div className="overflow-x-auto w-full">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('phoneNumbers.phoneNumber')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('common.status')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('phoneNumbers.purchased')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredPhoneNumbers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12">
                    <div className="text-center space-y-4">
                      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                          phone
                        </span>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('phoneNumbers.noPhoneNumbersFound')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                          {t('phoneNumbers.purchasePhoneNumbersDescription')}
                        </p>
                      </div>
                      <div className="pt-2">
                        <Button
                          variant="gradient-multi"
                          size="sm"
                          onClick={onBuyNumber}
                          className="inline-flex items-center gap-2"
                        >
                          <span className="material-icons text-sm">add</span>
                          {t('phoneNumbers.buyNumber')}
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPhoneNumbers.map(phone => (
                  <tr
                    key={phone.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-2 px-3">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {phone.number}
                      </div>
                    </td>
                    <td className="py-2 px-3">{getStatusBadge(phone.status)}</td>
                    <td className="py-2 px-3 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(phone.purchased_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(phone.id)}
                          className="p-1 h-8 w-8"
                          title="Delete Phone Number"
                        >
                          <span className="material-icons text-red-600 text-lg">delete</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
